
import React, { useState } from 'react'
import {Plus, Eye, Edit, Trash2, FileText, Download, Search, Filter, Calendar, DollarSign, Target, TrendingUp, Bell, Settings, History, BarChart3} from 'lucide-react'
import { useOrcamentos } from '../hooks/useOrcamentos'
import MetricsCard from '../components/MetricsCard'
import OrcamentoForm from '../components/OrcamentoForm'
import TemplateForm from '../components/TemplateForm'
import HistoricoOrcamento from '../components/HistoricoOrcamento'
import NotificationBell from '../components/NotificationBell'
import { exportToPDF, exportToExcel } from '../utils/exportUtils'

const Orcamentos: React.FC = () => {
  const {
    orcamentos,
    templates,
    historico,
    loading,
    metrics,
    createOrcamento,
    updateOrcamento,
    deleteOrcamento,
    createTemplate,
    updateTemplate,
    deleteTemplate
  } = useOrcamentos()

  const [showOrcamentoForm, setShowOrcamentoForm] = useState(false)
  const [showTemplateForm, setShowTemplateForm] = useState(false)
  const [editingOrcamento, setEditingOrcamento] = useState<any>(null)
  const [editingTemplate, setEditingTemplate] = useState<any>(null)
  const [viewingHistory, setViewingHistory] = useState<{ id: string; numero: string } | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<'orcamentos' | 'templates' | 'curva-abc'>('orcamentos')

  // Curva ABC states
  const [obraFilter, setObraFilter] = useState<string>('all')
  const [tipoFilter, setTipoFilter] = useState<string>('all')
  const [chartInsumoFilter, setChartInsumoFilter] = useState<string>('all')

  // Filter orçamentos
  const filteredOrcamentos = orcamentos.filter(orc => {
    const matchesSearch = orc.nomeCliente.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         orc.nomeProjeto.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         orc.numero.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = statusFilter === 'all' || orc.status === statusFilter
    return matchesSearch && matchesStatus
  })

  // Get unique obras from orçamentos
  const getObras = () => {
    const obras = new Set(orcamentos.map(orc => orc.nomeProjeto))
    return Array.from(obras)
  }

  // Get unique insumos for chart filter
  const getInsumos = () => {
    const insumos = new Set<string>()
    
    const filteredByObra = obraFilter === 'all' 
      ? orcamentos 
      : orcamentos.filter(orc => orc.nomeProjeto === obraFilter)

    filteredByObra.forEach(orcamento => {
      if (orcamento.etapas) {
        orcamento.etapas.forEach(etapa => {
          if (etapa.subetapas) {
            etapa.subetapas.forEach(subetapa => {
              if (subetapa.itens) {
                subetapa.itens.forEach(item => {
                  const itemTipo = item.tipo === 'insumo' ? 'material' : item.tipo
                  if (tipoFilter === 'all' || itemTipo === tipoFilter || item.tipo === tipoFilter) {
                    insumos.add(item.nome)
                  }
                })
              }
            })
          }
        })
      }
    })

    return Array.from(insumos).sort()
  }

  // Calculate Curva ABC data
  const calculateCurvaABC = () => {
    let allItems: any[] = []

    // Filter by obra if selected
    const filteredByObra = obraFilter === 'all' 
      ? orcamentos 
      : orcamentos.filter(orc => orc.nomeProjeto === obraFilter)

    // Extract all items from all orçamentos
    filteredByObra.forEach(orcamento => {
      if (orcamento.etapas) {
        orcamento.etapas.forEach(etapa => {
          if (etapa.subetapas) {
            etapa.subetapas.forEach(subetapa => {
              if (subetapa.itens) {
                subetapa.itens.forEach(item => {
                  // Filter by tipo if selected
                  const itemTipo = item.tipo === 'insumo' ? 'material' : item.tipo
                  if (tipoFilter === 'all' || itemTipo === tipoFilter || item.tipo === tipoFilter) {
                    // Filter by insumo name for chart if selected
                    if (chartInsumoFilter === 'all' || item.nome === chartInsumoFilter) {
                      allItems.push({
                        ...item,
                        obra: orcamento.nomeProjeto,
                        orcamento: orcamento.numero,
                        etapa: etapa.nome,
                        subetapa: subetapa.nome
                      })
                    }
                  }
                })
              }
            })
          }
        })
      }
    })

    // Group by item name and sum quantities/values
    const groupedItems = allItems.reduce((acc, item) => {
      const key = `${item.nome}_${item.tipo}`
      if (!acc[key]) {
        acc[key] = {
          nome: item.nome,
          tipo: item.tipo,
          unidadeMedida: item.unidadeMedida,
          quantidadeTotal: 0,
          valorTotal: 0,
          precoMedio: 0,
          obras: new Set(),
          ocorrencias: 0
        }
      }
      
      acc[key].quantidadeTotal += item.quantidade || 0
      acc[key].valorTotal += item.subtotal || 0
      acc[key].obras.add(item.obra)
      acc[key].ocorrencias += 1
      
      return acc
    }, {})

    // Convert to array and calculate average price
    const itemsArray = Object.values(groupedItems).map((item: any) => ({
      ...item,
      precoMedio: item.quantidadeTotal > 0 ? item.valorTotal / item.quantidadeTotal : 0,
      obras: Array.from(item.obras)
    }))

    // Sort by total value (descending)
    itemsArray.sort((a: any, b: any) => b.valorTotal - a.valorTotal)

    // Calculate cumulative percentage
    const totalValue = itemsArray.reduce((sum: number, item: any) => sum + item.valorTotal, 0)
    let cumulativeValue = 0
    
    return itemsArray.map((item: any, index) => {
      cumulativeValue += item.valorTotal
      const percentualIndividual = totalValue > 0 ? (item.valorTotal / totalValue) * 100 : 0
      const percentualAcumulado = totalValue > 0 ? (cumulativeValue / totalValue) * 100 : 0
      
      let classificacao = 'C'
      if (percentualAcumulado <= 80) classificacao = 'A'
      else if (percentualAcumulado <= 95) classificacao = 'B'
      
      return {
        ...item,
        posicao: index + 1,
        percentualIndividual,
        percentualAcumulado,
        classificacao
      }
    })
  }

  // Generate chart data for Curva ABC
  const generateChartData = () => {
    const curvaData = calculateCurvaABC()
    
    if (curvaData.length === 0) return null

    // Prepare data for chart visualization
    const chartData = curvaData.slice(0, 20).map((item: any) => ({
      nome: item.nome.length > 20 ? item.nome.substring(0, 20) + '...' : item.nome,
      valor: item.valorTotal,
      percentualAcumulado: item.percentualAcumulado,
      classificacao: item.classificacao
    }))

    return chartData
  }

  // Render chart visualization
  const renderChart = () => {
    const chartData = generateChartData()
    
    if (!chartData || chartData.length === 0) {
      return (
        <div className="text-center py-8">
          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
          <p className="mt-2 text-gray-500">Nenhum dado disponível para o gráfico</p>
        </div>
      )
    }

    const maxValue = Math.max(...chartData.map(item => item.valor))

    return (
      <div className="bg-white p-6 rounded-lg border">
        <h4 className="text-lg font-semibold mb-4">Gráfico Curva ABC</h4>
        <div className="space-y-3">
          {chartData.map((item, index) => (
            <div key={index} className="flex items-center space-x-3">
              <div className="w-32 text-sm font-medium text-gray-700 truncate" title={item.nome}>
                {item.nome}
              </div>
              <div className="flex-1 relative">
                <div className="w-full bg-gray-200 rounded-full h-6">
                  <div 
                    className={`h-6 rounded-full flex items-center justify-end pr-2 text-xs font-semibold text-white ${
                      item.classificacao === 'A' ? 'bg-red-500' :
                      item.classificacao === 'B' ? 'bg-yellow-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(item.valor / maxValue) * 100}%` }}
                  >
                    {item.percentualAcumulado.toFixed(1)}%
                  </div>
                </div>
              </div>
              <div className="w-24 text-sm text-gray-600 text-right">
                {formatCurrency(item.valor)}
              </div>
              <div className="w-16 text-center">
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClassificationColor(item.classificacao)}`}>
                  {item.classificacao}
                </span>
              </div>
            </div>
          ))}
        </div>
        
        {/* Legend */}
        <div className="mt-6 flex justify-center space-x-6 text-sm">
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-red-500 rounded"></div>
            <span>Classe A (0-80%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-yellow-500 rounded"></div>
            <span>Classe B (80-95%)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-4 h-4 bg-green-500 rounded"></div>
            <span>Classe C (95-100%)</span>
          </div>
        </div>
      </div>
    )
  }

  // Export Curva ABC to Excel
  const exportCurvaABCToExcel = () => {
    try {
      const curvaData = calculateCurvaABC()
      
      const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(value)
      }

      let csvContent = ''
      
      // Header information
      csvContent += `CURVA ABC - ANÁLISE DE ITENS\n`
      csvContent += `Obra: ${obraFilter === 'all' ? 'Todas as Obras' : obraFilter}\n`
      csvContent += `Tipo: ${tipoFilter === 'all' ? 'Todos os Tipos' : tipoFilter}\n`
      csvContent += `Insumo: ${chartInsumoFilter === 'all' ? 'Todos os Insumos' : chartInsumoFilter}\n`
      csvContent += `Data da Análise: ${new Date().toLocaleDateString('pt-BR')}\n`
      csvContent += `Total de Itens: ${curvaData.length}\n\n`

      // Table header
      csvContent += 'Posição,Item,Tipo,Unidade,Qtd Total,Valor Total,Preço Médio,% Individual,% Acumulado,Classificação,Ocorrências,Obras\n'

      // Data rows
      curvaData.forEach((item: any) => {
        csvContent += `${item.posicao},"${item.nome}",${item.tipo},${item.unidadeMedida},${item.quantidadeTotal.toFixed(2)},${item.valorTotal.toFixed(2)},${item.precoMedio.toFixed(2)},${item.percentualIndividual.toFixed(2)}%,${item.percentualAcumulado.toFixed(2)}%,${item.classificacao},${item.ocorrencias},"${item.obras.join('; ')}"\n`
      })

      // Summary
      csvContent += '\n'
      csvContent += 'RESUMO POR CLASSIFICAÇÃO\n'
      const resumo = curvaData.reduce((acc: any, item: any) => {
        if (!acc[item.classificacao]) {
          acc[item.classificacao] = { count: 0, value: 0 }
        }
        acc[item.classificacao].count += 1
        acc[item.classificacao].value += item.valorTotal
        return acc
      }, {})

      Object.entries(resumo).forEach(([classe, data]: any) => {
        const totalValue = curvaData.reduce((sum: number, item: any) => sum + item.valorTotal, 0)
        const percentage = totalValue > 0 ? (data.value / totalValue) * 100 : 0
        csvContent += `Classe ${classe},${data.count} itens,${formatCurrency(data.value)},${percentage.toFixed(2)}%\n`
      })

      // Create and download file
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      
      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `curva_abc_${obraFilter === 'all' ? 'todas_obras' : obraFilter.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Erro ao exportar Curva ABC:', error)
      alert('Erro ao exportar Curva ABC. Tente novamente.')
    }
  }

  // Calculate percentage change
  const calculatePercentageChange = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0
    return ((current - previous) / previous) * 100
  }

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value)
  }

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'rascunho': return 'bg-gray-100 text-gray-800'
      case 'enviado': return 'bg-blue-100 text-blue-800'
      case 'aprovado': return 'bg-green-100 text-green-800'
      case 'perdido': return 'bg-red-100 text-red-800'
      case 'expirado': return 'bg-orange-100 text-orange-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Get status label
  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'rascunho': return 'Rascunho'
      case 'enviado': return 'Enviado'
      case 'aprovado': return 'Aprovado'
      case 'perdido': return 'Perdido'
      case 'expirado': return 'Expirado'
      default: return status
    }
  }

  // Get classification color
  const getClassificationColor = (classificacao: string) => {
    switch (classificacao) {
      case 'A': return 'bg-red-100 text-red-800'
      case 'B': return 'bg-yellow-100 text-yellow-800'
      case 'C': return 'bg-green-100 text-green-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  // Handle delete
  const handleDelete = async (id: string, type: 'orcamento' | 'template') => {
    if (confirm(`Tem certeza que deseja excluir este ${type}?`)) {
      if (type === 'orcamento') {
        await deleteOrcamento(id)
      } else {
        await deleteTemplate(id)
      }
    }
  }

  // Handle view history
  const handleViewHistory = (orcamento: any) => {
    setViewingHistory({
      id: orcamento._id,
      numero: orcamento.numero
    })
  }

  // Handle export to PDF
  const handleExportToPDF = (orcamento: any) => {
    try {
      exportToPDF(orcamento)
    } catch (error) {
      console.error('Erro ao exportar PDF:', error)
    }
  }

  // Handle export to Excel
  const handleExportToExcel = (orcamento: any) => {
    try {
      exportToExcel(orcamento)
    } catch (error) {
      console.error('Erro ao exportar Excel:', error)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando orçamentos...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Orçamentos</h1>
              <p className="text-gray-600 mt-1">Gestão completa de orçamentos e propostas</p>
            </div>
            <div className="flex items-center gap-4">
              <NotificationBell />
              <button
                onClick={() => setShowOrcamentoForm(true)}
                className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 transition-colors"
              >
                <Plus size={20} />
                Novo Orçamento
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* Métricas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <MetricsCard
            title="Total de Orçamentos"
            value={metrics.totalOrcamentos}
            icon={FileText}
            color="#3B82F6"
            description={`${calculatePercentageChange(metrics.totalOrcamentos, metrics.totalOrcamentosAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.totalOrcamentos, metrics.totalOrcamentosAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Valor Total"
            value={formatCurrency(metrics.valorTotal)}
            icon={DollarSign}
            color="#10B981"
            description={`${calculatePercentageChange(metrics.valorTotal, metrics.valorTotalAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.valorTotal, metrics.valorTotalAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Aprovados"
            value={metrics.quantidadeAprovada}
            icon={Target}
            color="#059669"
            description={`${calculatePercentageChange(metrics.quantidadeAprovada, metrics.quantidadeAprovadaAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.quantidadeAprovada, metrics.quantidadeAprovadaAnterior).toFixed(1)}% vs mês anterior`}
          />
          <MetricsCard
            title="Enviados"
            value={metrics.quantidadeEnviada}
            icon={TrendingUp}
            color="#F59E0B"
            description={`${calculatePercentageChange(metrics.quantidadeEnviada, metrics.quantidadeEnviadaAnterior) >= 0 ? '+' : ''}${calculatePercentageChange(metrics.quantidadeEnviada, metrics.quantidadeEnviadaAnterior).toFixed(1)}% vs mês anterior`}
          />
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('orcamentos')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'orcamentos'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Orçamentos ({orcamentos.length})
              </button>
              <button
                onClick={() => setActiveTab('templates')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'templates'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Templates ({templates.filter(t => t.ativo).length})
              </button>
              <button
                onClick={() => setActiveTab('curva-abc')}
                className={`py-4 px-6 border-b-2 font-medium text-sm ${
                  activeTab === 'curva-abc'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <BarChart3 size={16} className="inline mr-2" />
                Curva ABC
              </button>
            </nav>
          </div>

          {/* Orçamentos Tab */}
          {activeTab === 'orcamentos' && (
            <>
              {/* Filtros e Busca */}
              <div className="p-6 border-b">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                    <input
                      type="text"
                      placeholder="Buscar por cliente, projeto ou número..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Filter size={20} className="text-gray-400" />
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Todos os Status</option>
                      <option value="rascunho">Rascunho</option>
                      <option value="enviado">Enviado</option>
                      <option value="aprovado">Aprovado</option>
                      <option value="perdido">Perdido</option>
                      <option value="expirado">Expirado</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Lista de Orçamentos */}
              <div className="overflow-x-auto">
                {filteredOrcamentos.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum orçamento encontrado</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {searchTerm || statusFilter !== 'all' 
                        ? 'Tente ajustar os filtros de busca'
                        : 'Comece criando seu primeiro orçamento'
                      }
                    </p>
                  </div>
                ) : (
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Número
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Cliente
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Projeto
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Valor Total
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Data Emissão
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Validade
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredOrcamentos.map((orcamento) => (
                        <tr key={orcamento._id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">{orcamento.numero}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{orcamento.nomeCliente}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{orcamento.nomeProjeto}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatCurrency(orcamento.valorTotal)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(orcamento.status)}`}>
                              {getStatusLabel(orcamento.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(orcamento.dataEmissao)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatDate(orcamento.dataValidade)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => handleViewHistory(orcamento)}
                                className="text-purple-600 hover:text-purple-900 p-1 rounded hover:bg-purple-50"
                                title="Ver Histórico"
                              >
                                <History size={16} />
                              </button>
                              <button
                                onClick={() => setEditingOrcamento(orcamento)}
                                className="text-yellow-600 hover:text-yellow-900 p-1 rounded hover:bg-yellow-50"
                                title="Editar"
                              >
                                <Edit size={16} />
                              </button>
                              <button
                                onClick={() => handleExportToPDF(orcamento)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded hover:bg-blue-50"
                                title="Exportar PDF"
                              >
                                <Download size={16} />
                              </button>
                              <button
                                onClick={() => handleExportToExcel(orcamento)}
                                className="text-green-600 hover:text-green-900 p-1 rounded hover:bg-green-50"
                                title="Exportar Excel"
                              >
                                <FileText size={16} />
                              </button>
                              <button
                                onClick={() => handleDelete(orcamento._id, 'orcamento')}
                                className="text-red-600 hover:text-red-900 p-1 rounded hover:bg-red-50"
                                title="Excluir"
                              >
                                <Trash2 size={16} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}

          {/* Templates Tab */}
          {activeTab === 'templates' && (
            <>
              <div className="p-6 border-b">
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-semibold">Templates de Orçamento</h3>
                  <button
                    onClick={() => setShowTemplateForm(true)}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
                  >
                    <Plus size={16} />
                    Novo Template
                  </button>
                </div>
              </div>

              <div className="p-6">
                {templates.length === 0 ? (
                  <div className="text-center py-12">
                    <Settings className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum template criado</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Crie templates para agilizar a criação de orçamentos
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {templates.map(template => (
                      <div key={template._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow">
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{template.nome}</h4>
                            <p className="text-sm text-gray-600">{template.categoria}</p>
                          </div>
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            template.ativo ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                          }`}>
                            {template.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-4">{template.descricao}</p>
                        
                        <div className="flex justify-between items-center mb-4">
                          <span className="text-sm text-gray-500">BDI: {template.bdi}%</span>
                          <span className="text-sm text-gray-500">{template.etapas.length} etapas</span>
                        </div>

                        <div className="flex space-x-2">
                          <button
                            onClick={() => setEditingTemplate(template)}
                            className="flex-1 bg-yellow-600 text-white px-3 py-2 rounded-md hover:bg-yellow-700 text-sm"
                          >
                            Editar
                          </button>
                          <button
                            onClick={() => handleDelete(template._id, 'template')}
                            className="bg-red-600 text-white px-3 py-2 rounded-md hover:bg-red-700 text-sm"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {/* Curva ABC Tab */}
          {activeTab === 'curva-abc' && (
            <>
              <div className="p-6 border-b">
                <div className="flex flex-col gap-6">
                  <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
                    <div>
                      <h3 className="text-lg font-semibold">Análise Curva ABC</h3>
                      <p className="text-sm text-gray-600">Análise de itens por valor e importância</p>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Filtro por Obra */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Obra:</label>
                        <select
                          value={obraFilter}
                          onChange={(e) => setObraFilter(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="all">Todas as Obras</option>
                          {getObras().map(obra => (
                            <option key={obra} value={obra}>{obra}</option>
                          ))}
                        </select>
                      </div>

                      {/* Filtro por Tipo */}
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-700">Tipo:</label>
                        <select
                          value={tipoFilter}
                          onChange={(e) => setTipoFilter(e.target.value)}
                          className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="all">Todos os Tipos</option>
                          <option value="material">Materiais</option>
                          <option value="mao_obra">Mão de Obra</option>
                          <option value="equipamento">Equipamentos</option>
                          <option value="composicao">Composição</option>
                        </select>
                      </div>

                      {/* Botão de Exportar */}
                      <button
                        onClick={exportCurvaABCToExcel}
                        className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 flex items-center gap-2"
                      >
                        <Download size={16} />
                        Exportar Excel
                      </button>
                    </div>
                  </div>

                  {/* Filtro para o Gráfico */}
                  <div className="flex items-center gap-2">
                    <label className="text-sm font-medium text-gray-700">Filtro do Gráfico - Insumo:</label>
                    <select
                      value={chartInsumoFilter}
                      onChange={(e) => setChartInsumoFilter(e.target.value)}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="all">Todos os Insumos</option>
                      {getInsumos().map(insumo => (
                        <option key={insumo} value={insumo}>{insumo}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="p-6">
                {/* Gráfico da Curva ABC */}
                <div className="mb-8">
                  {renderChart()}
                </div>

                {/* Tabela da Curva ABC */}
                <div className="overflow-x-auto">
                  {(() => {
                    const curvaData = calculateCurvaABC()
                    
                    if (curvaData.length === 0) {
                      return (
                        <div className="text-center py-12">
                          <BarChart3 className="mx-auto h-12 w-12 text-gray-400" />
                          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum item encontrado</h3>
                          <p className="mt-1 text-sm text-gray-500">
                            Ajuste os filtros ou adicione itens aos orçamentos
                          </p>
                        </div>
                      )
                    }

                    return (
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Posição
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Item
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Tipo
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Qtd Total
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Valor Total
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              % Individual
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              % Acumulado
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Classificação
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Ocorrências
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Obras
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {curvaData.map((item: any) => (
                            <tr key={`${item.nome}_${item.tipo}`} className="hover:bg-gray-50">
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">{item.posicao}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{item.nome}</div>
                                <div className="text-sm text-gray-500">{item.unidadeMedida}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900 capitalize">{item.tipo.replace('_', ' ')}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{item.quantidadeTotal.toFixed(2)}</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm font-medium text-gray-900">
                                  {formatCurrency(item.valorTotal)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{item.percentualIndividual.toFixed(2)}%</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{item.percentualAcumulado.toFixed(2)}%</div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getClassificationColor(item.classificacao)}`}>
                                  Classe {item.classificacao}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-900">{item.ocorrencias}</div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">
                                  {item.obras.slice(0, 2).join(', ')}
                                  {item.obras.length > 2 && (
                                    <span className="text-gray-500"> +{item.obras.length - 2} mais</span>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )
                  })()}
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <OrcamentoForm
        isOpen={showOrcamentoForm}
        onClose={() => setShowOrcamentoForm(false)}
        onSubmit={createOrcamento}
        title="Criar Novo Orçamento"
      />

      <OrcamentoForm
        isOpen={!!editingOrcamento}
        onClose={() => setEditingOrcamento(null)}
        onSubmit={(data) => updateOrcamento(editingOrcamento._id, data)}
        initialData={editingOrcamento}
        title="Editar Orçamento"
      />

      <TemplateForm
        isOpen={showTemplateForm}
        onClose={() => setShowTemplateForm(false)}
        onSubmit={createTemplate}
        title="Criar Novo Template"
      />

      <TemplateForm
        isOpen={!!editingTemplate}
        onClose={() => setEditingTemplate(null)}
        onSubmit={(data) => updateTemplate(editingTemplate._id, data)}
        initialData={editingTemplate}
        title="Editar Template"
      />

      <HistoricoOrcamento
        isOpen={!!viewingHistory}
        onClose={() => setViewingHistory(null)}
        orcamentoId={viewingHistory?.id || ''}
        orcamentoNumero={viewingHistory?.numero || ''}
      />
    </div>
  )
}

export default Orcamentos
